/**
 * AIQuizPark Analytics Service
 * Comprehensive conversion funnel and engagement tracking
 * 
 * Dual Funnels:
 * - Student: Homepage → Student CTA → Quiz Start → Quiz Complete → Signup → Repeat → Pricing → Checkout → Paid
 * - Teacher: Homepage → Teacher CTA → Quiz Create → Quiz Share → Signup → Class Used → Pricing → Checkout → Paid
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Initialize PostHog if configured
let posthogInitialized = false;

export const initPostHog = () => {
  if (posthogInitialized || !POSTHOG_API_KEY || typeof window === 'undefined') return;
  
  // Load PostHog script
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  
  window.posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false, // We'll track events explicitly
    persistence: 'localStorage',
  });
  
  posthogInitialized = true;
  console.log('[Analytics] PostHog initialized');
};

// =============================================================================
// UTM TRACKING
// =============================================================================

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const UTM_STORAGE_KEY = 'aiquizpark_utm';
const USER_JOURNEY_KEY = 'aiquizpark_journey';

/**
 * Capture UTM parameters from URL and store them
 */
export const captureUTMParams = () => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {};
  let hasUTM = false;
  
  UTM_PARAMS.forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmData[param] = value;
      hasUTM = true;
    }
  });
  
  // Store if we have new UTM data
  if (hasUTM) {
    utmData.captured_at = new Date().toISOString();
    utmData.landing_page = window.location.pathname;
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    console.log('[Analytics] UTM captured:', utmData);
  }
  
  return getStoredUTM();
};

/**
 * Get stored UTM parameters
 */
export const getStoredUTM = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Clear UTM after conversion (optional)
 */
export const clearUTM = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(UTM_STORAGE_KEY);
};

// =============================================================================
// USER JOURNEY TRACKING
// =============================================================================

/**
 * Track user journey state
 */
export const getUserJourney = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(USER_JOURNEY_KEY);
    return stored ? JSON.parse(stored) : {
      audience: null, // 'student' or 'teacher'
      first_visit: new Date().toISOString(),
      quizzes_started: 0,
      quizzes_completed: 0,
      signed_up: false,
      viewed_pricing: false,
    };
  } catch {
    return {};
  }
};

export const updateUserJourney = (updates) => {
  if (typeof window === 'undefined') return;
  const journey = getUserJourney();
  const updated = { ...journey, ...updates, last_activity: new Date().toISOString() };
  localStorage.setItem(USER_JOURNEY_KEY, JSON.stringify(updated));
  return updated;
};

// =============================================================================
// EVENT NAMES
// =============================================================================

export const ANALYTICS_EVENTS = {
  // Page Views
  PAGE_VIEW: 'page_view',
  HOMEPAGE_VISIT: 'homepage_visit',
  
  // Landing Page CTAs
  STUDENT_CTA_CLICKED: 'student_cta_clicked',
  TEACHER_CTA_CLICKED: 'teacher_cta_clicked',
  
  // Trial
  TRIAL_STARTED: 'trial_started',
  
  // Quiz Funnel
  QUIZ_GENERATION_STARTED: 'quiz_generation_started',
  QUIZ_GENERATED_SUCCESS: 'quiz_generated_success',
  QUIZ_GENERATION_FAILED: 'quiz_generation_failed',
  QUIZ_ABANDONED: 'quiz_abandoned',
  QUIZ_STARTED: 'quiz_started',
  QUIZ_COMPLETED: 'quiz_completed',
  SECOND_QUIZ_CREATED: 'second_quiz_created',
  
  // Quiz Progress Tracking (Abandonment Funnel)
  STUDY_MATERIAL_SELECTED: 'study_material_selected',
  STUDY_MATERIAL_UPLOADED: 'study_material_uploaded',
  QUIZ_OPENED: 'quiz_opened',
  QUIZ_FIRST_ANSWER: 'quiz_first_answer',
  QUIZ_PROGRESS_25: 'quiz_progress_25',
  QUIZ_PROGRESS_50: 'quiz_progress_50',
  QUIZ_PROGRESS_75: 'quiz_progress_75',
  
  // Signup Funnel
  STUDENT_SIGNUP: 'student_signup',
  TEACHER_SIGNUP: 'teacher_signup',
  
  // Engagement Events
  PRACTICE_MISTAKES_OPENED: 'practice_mistakes_opened',
  PRACTICE_MISTAKES_COMPLETED: 'practice_mistakes_completed',
  AI_QUESTION_SOLVER_USED: 'ai_question_solver_used',
  EXAM_PRESET_SELECTED: 'exam_preset_selected',
  QUIZ_SHARED: 'quiz_shared',
  ASSIGNMENT_CREATED: 'assignment_created',
  CLASS_CREATED: 'class_created',
  PDF_EXPORTED: 'pdf_exported',
  
  // Pricing Funnel
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  STUDENT_CHECKOUT_STARTED: 'student_checkout_started',
  TEACHER_CHECKOUT_STARTED: 'teacher_checkout_started',
  GROUP_CHECKOUT_STARTED: 'group_checkout_started',
  
  // Payment Completion
  PAYMENT_COMPLETED: 'payment_completed',
  
  // Quick Challenge (Landing Page Demo)
  QUICK_CHALLENGE_STARTED: 'quick_challenge_started',
  QUICK_CHALLENGE_ANSWER_SELECTED: 'quick_challenge_answer_selected',
  QUICK_CHALLENGE_EXPLANATION_VIEWED: 'quick_challenge_explanation_viewed',
  QUICK_CHALLENGE_COMPLETED: 'quick_challenge_completed',
  
  // Navigation
  NAV_STUDENTS_CLICKED: 'nav_students_clicked',
  NAV_TEACHERS_CLICKED: 'nav_teachers_clicked',
  NAV_DEMO_CLICKED: 'nav_demo_clicked',
  NAV_PRICING_CLICKED: 'nav_pricing_clicked',
  
  // Return Visits
  RETURNING_USER: 'returning_user',
};

// =============================================================================
// CORE TRACKING FUNCTION
// =============================================================================

/**
 * Track an analytics event
 * @param {string} eventName - Name of the event
 * @param {object} eventData - Additional data to track
 * @param {boolean} sendToBackend - Whether to also send to backend for aggregation
 */
export const trackEvent = async (eventName, eventData = {}, sendToBackend = true) => {
  const utm = getStoredUTM();
  const journey = getUserJourney();
  
  // Enrich event data
  const enrichedData = {
    ...eventData,
    timestamp: new Date().toISOString(),
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? (document.referrer || 'direct') : 'unknown',
    // UTM data
    ...utm,
    // Journey context
    audience: journey.audience || eventData.audience || 'unknown',
    user_journey_stage: determineJourneyStage(journey),
  };

  // Remove sensitive data
  delete enrichedData.email;
  delete enrichedData.password;
  delete enrichedData.token;

  // PostHog
  if (typeof window !== 'undefined' && window.posthog) {
    try {
      window.posthog.capture(eventName, enrichedData);
    } catch (e) {
      console.warn('[Analytics] PostHog error:', e);
    }
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, enrichedData);
    } catch (e) {
      console.warn('[Analytics] GA4 error:', e);
    }
  }

  // Send to backend for aggregation (non-blocking)
  if (sendToBackend && API_URL) {
    sendEventToBackend(eventName, enrichedData).catch(() => {});
  }

  // Console log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] ${eventName}`, enrichedData);
  }
};

/**
 * Determine user's journey stage
 */
const determineJourneyStage = (journey) => {
  if (!journey.audience) return 'visitor';
  if (!journey.quizzes_started) return 'interested';
  if (!journey.quizzes_completed) return 'trying';
  if (!journey.signed_up) return 'engaged';
  if (!journey.viewed_pricing) return 'active_user';
  return 'potential_customer';
};

/**
 * Send event to backend for aggregation
 */
const sendEventToBackend = async (eventName, eventData) => {
  try {
    await fetch(`${API_URL}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_name: eventName, event_data: eventData }),
    });
  } catch (e) {
    // Silently fail - analytics should never break the app
  }
};

// =============================================================================
// SPECIFIC TRACKING FUNCTIONS
// =============================================================================

// Homepage & Navigation
export const trackHomepageVisit = () => {
  captureUTMParams();
  const journey = getUserJourney();
  
  if (journey.first_visit && new Date(journey.first_visit) < new Date(Date.now() - 24*60*60*1000)) {
    trackEvent(ANALYTICS_EVENTS.RETURNING_USER);
  }
  
  trackEvent(ANALYTICS_EVENTS.HOMEPAGE_VISIT);
};

// CTA Clicks
export const trackStudentCTA = (location = 'unknown') => {
  updateUserJourney({ audience: 'student' });
  trackEvent(ANALYTICS_EVENTS.STUDENT_CTA_CLICKED, { location, audience: 'student' });
};

export const trackTeacherCTA = (location = 'unknown') => {
  updateUserJourney({ audience: 'teacher' });
  trackEvent(ANALYTICS_EVENTS.TEACHER_CTA_CLICKED, { location, audience: 'teacher' });
};

// Trial
export const trackTrialStarted = (audience = 'student') => {
  updateUserJourney({ audience, trial_started: true });
  trackEvent(ANALYTICS_EVENTS.TRIAL_STARTED, { audience });
};

// Quiz Events
export const trackQuizGenerationStarted = (inputType, userType = 'unknown') => {
  const journey = updateUserJourney({ quizzes_started: (getUserJourney().quizzes_started || 0) + 1 });
  
  trackEvent(ANALYTICS_EVENTS.QUIZ_GENERATION_STARTED, { 
    inputType, 
    userType,
    quiz_number: journey.quizzes_started,
  });
  
  // Track second quiz milestone
  if (journey.quizzes_started === 2) {
    trackEvent(ANALYTICS_EVENTS.SECOND_QUIZ_CREATED, { userType });
  }
};

export const trackQuizGeneratedSuccess = (questionCount, inputType, userType = 'unknown') => {
  trackEvent(ANALYTICS_EVENTS.QUIZ_GENERATED_SUCCESS, { questionCount, inputType, userType });
};

export const trackQuizGenerationFailed = (inputType, errorType, userType = 'unknown') => {
  trackEvent(ANALYTICS_EVENTS.QUIZ_GENERATION_FAILED, { inputType, errorType, userType });
};

export const trackQuizStarted = (quizId, questionCount, userType = 'unknown') => {
  trackEvent(ANALYTICS_EVENTS.QUIZ_STARTED, { quizId, questionCount, userType });
};

export const trackQuizCompleted = (quizId, questionCount, score, userType = 'unknown') => {
  const journey = updateUserJourney({ quizzes_completed: (getUserJourney().quizzes_completed || 0) + 1 });
  
  trackEvent(ANALYTICS_EVENTS.QUIZ_COMPLETED, { 
    quizId,
    questionCount, 
    score, 
    userType,
    completion_number: journey.quizzes_completed,
  });
};

export const trackQuizAbandoned = (quizId, questionCount, answeredCount, userType = 'unknown') => {
  trackEvent(ANALYTICS_EVENTS.QUIZ_ABANDONED, { 
    quizId, 
    questionCount, 
    answeredCount,
    abandon_percentage: Math.round((answeredCount / questionCount) * 100),
    userType 
  });
};

// Study Material
export const trackStudyMaterialSelected = (inputType) => {
  trackEvent(ANALYTICS_EVENTS.STUDY_MATERIAL_SELECTED, { inputType });
};

export const trackStudyMaterialUploaded = (fileType, fileSizeKB) => {
  // Only track file type and approximate size, no content
  trackEvent(ANALYTICS_EVENTS.STUDY_MATERIAL_UPLOADED, { 
    fileType, 
    fileSizeRange: fileSizeKB < 100 ? 'small' : fileSizeKB < 1000 ? 'medium' : 'large'
  });
};

// Quiz Progress Tracking (for abandonment analysis)
export const trackQuizProgress = {
  opened: (quizId, questionCount) => {
    trackEvent(ANALYTICS_EVENTS.QUIZ_OPENED, { quizId, questionCount });
  },
  
  firstAnswer: (quizId, questionCount) => {
    trackEvent(ANALYTICS_EVENTS.QUIZ_FIRST_ANSWER, { quizId, questionCount });
  },
  
  progress: (quizId, questionCount, answeredCount) => {
    const percentage = Math.round((answeredCount / questionCount) * 100);
    
    // Track milestone events (only fire once per milestone)
    if (percentage >= 25 && percentage < 50) {
      trackEvent(ANALYTICS_EVENTS.QUIZ_PROGRESS_25, { quizId, questionCount, answeredCount });
    } else if (percentage >= 50 && percentage < 75) {
      trackEvent(ANALYTICS_EVENTS.QUIZ_PROGRESS_50, { quizId, questionCount, answeredCount });
    } else if (percentage >= 75 && percentage < 100) {
      trackEvent(ANALYTICS_EVENTS.QUIZ_PROGRESS_75, { quizId, questionCount, answeredCount });
    }
  },
};

// Signup
export const trackSignup = (role, source = 'register_page') => {
  updateUserJourney({ signed_up: true, signup_role: role });
  const eventName = role === 'teacher' ? ANALYTICS_EVENTS.TEACHER_SIGNUP : ANALYTICS_EVENTS.STUDENT_SIGNUP;
  trackEvent(eventName, { role, source });
};

// Engagement Events
export const trackPracticeMistakesOpened = (questionCount) => {
  trackEvent(ANALYTICS_EVENTS.PRACTICE_MISTAKES_OPENED, { questionCount });
};

export const trackPracticeMistakesCompleted = (questionCount, score) => {
  trackEvent(ANALYTICS_EVENTS.PRACTICE_MISTAKES_COMPLETED, { questionCount, score });
};

export const trackAIQuestionSolverUsed = (inputType) => {
  trackEvent(ANALYTICS_EVENTS.AI_QUESTION_SOLVER_USED, { inputType });
};

export const trackExamPresetSelected = (presetId, presetName) => {
  trackEvent(ANALYTICS_EVENTS.EXAM_PRESET_SELECTED, { presetId, presetName });
};

export const trackQuizShared = (shareMethod) => {
  trackEvent(ANALYTICS_EVENTS.QUIZ_SHARED, { shareMethod });
};

export const trackAssignmentCreated = (classId, dueDate) => {
  trackEvent(ANALYTICS_EVENTS.ASSIGNMENT_CREATED, { classId, hasDueDate: !!dueDate });
};

export const trackClassCreated = () => {
  trackEvent(ANALYTICS_EVENTS.CLASS_CREATED);
};

export const trackPDFExported = () => {
  trackEvent(ANALYTICS_EVENTS.PDF_EXPORTED);
};

// Pricing Funnel
export const trackPricingView = (source = 'direct') => {
  updateUserJourney({ viewed_pricing: true });
  trackEvent(ANALYTICS_EVENTS.PRICING_PAGE_VIEWED, { source });
};

export const trackCheckoutStarted = (planType, gateway, price) => {
  let eventName;
  switch (planType) {
    case 'teacher':
      eventName = ANALYTICS_EVENTS.TEACHER_CHECKOUT_STARTED;
      break;
    case 'group':
      eventName = ANALYTICS_EVENTS.GROUP_CHECKOUT_STARTED;
      break;
    default:
      eventName = ANALYTICS_EVENTS.STUDENT_CHECKOUT_STARTED;
  }
  trackEvent(eventName, { planType, gateway, price });
};

export const trackPaymentCompleted = (planType, gateway, price) => {
  trackEvent(ANALYTICS_EVENTS.PAYMENT_COMPLETED, { planType, gateway, price });
};

// Quick Challenge
export const trackQuickChallenge = {
  started: () => trackEvent(ANALYTICS_EVENTS.QUICK_CHALLENGE_STARTED),
  answerSelected: (questionIndex, isCorrect) => 
    trackEvent(ANALYTICS_EVENTS.QUICK_CHALLENGE_ANSWER_SELECTED, { questionIndex, isCorrect }),
  explanationViewed: (questionIndex) => 
    trackEvent(ANALYTICS_EVENTS.QUICK_CHALLENGE_EXPLANATION_VIEWED, { questionIndex }),
  completed: (score, totalQuestions) => 
    trackEvent(ANALYTICS_EVENTS.QUICK_CHALLENGE_COMPLETED, { score, totalQuestions }),
};

// =============================================================================
// USER IDENTIFICATION
// =============================================================================

/**
 * Identify user for tracking (call after login/signup)
 */
export const identifyUser = (userId, traits = {}) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.identify(userId, {
      ...traits,
      ...getStoredUTM(),
    });
  }
};

/**
 * Reset user identity (call on logout)
 */
export const resetUser = () => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.reset();
  }
};

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  initPostHog,
  captureUTMParams,
  getStoredUTM,
  clearUTM,
  getUserJourney,
  updateUserJourney,
  trackEvent,
  trackHomepageVisit,
  trackStudentCTA,
  trackTeacherCTA,
  trackTrialStarted,
  trackQuizGenerationStarted,
  trackQuizGeneratedSuccess,
  trackQuizGenerationFailed,
  trackQuizStarted,
  trackQuizCompleted,
  trackQuizAbandoned,
  trackStudyMaterialSelected,
  trackStudyMaterialUploaded,
  trackQuizProgress,
  trackSignup,
  trackPracticeMistakesOpened,
  trackPracticeMistakesCompleted,
  trackAIQuestionSolverUsed,
  trackExamPresetSelected,
  trackQuizShared,
  trackAssignmentCreated,
  trackClassCreated,
  trackPDFExported,
  trackPricingView,
  trackCheckoutStarted,
  trackPaymentCompleted,
  trackQuickChallenge,
  identifyUser,
  resetUser,
  ANALYTICS_EVENTS,
};
